import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Save, X, MapPin, Upload, Trash2 } from 'lucide-react';
import { ImageWithFallback } from '@/components/ImageWithFallback';
import { useParams, useNavigate } from 'react-router-dom';
import { branchsAPI, type BaseCompanyBranch } from '@/services/branchs';
import { uploadFile, type UploadProgressInfo } from '@/services/upload';

interface BranchFormData extends Omit<BaseCompanyBranch, 'imageUrls'> {
  imageUrls: string[];
  uploadedFiles: Array<{ file: File; progress: number; url?: string }>;
}

export function BranchAddEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const mode = id ? 'edit' : 'add';

  // Get branch data if in edit mode
  const { data: branchData, isLoading } = useQuery({
    queryKey: ['branch', id],
    queryFn: () => branchsAPI.getBranchById(id!),
    enabled: !!id,
  });

  // Initialize form with branch data if in edit mode
  useEffect(() => {
    if (branchData && mode === 'edit') {
      setFormData((prev) => ({
        ...prev,
        name: branchData.name || '',
        address: branchData.address || '',
        phoneNumber: branchData.phoneNumber || '',
        isActive: branchData.isActive ?? true,
        imageUrls: branchData.imageUrls || [],
        uploadedFiles: [],
      }));
    }
  }, [branchData, mode]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<BranchFormData>({
    name: '',
    address: '',
    phoneNumber: '',
    isActive: true,
    imageUrls: [],
    uploadedFiles: [],
  });

  useEffect(() => {
    if (mode === 'edit' && branchData) {
      setFormData((prev) => ({
        ...prev,
        name: branchData.name || '',
        address: branchData.address || '',
        phoneNumber: branchData.phoneNumber || '',
        isActive: branchData.isActive ?? true,
        imageUrls: branchData.imageUrls || [],
        // Initialize uploadedFiles with existing image URLs
        uploadedFiles: (branchData.imageUrls || []).map((url) => ({
          file: new File([], url.split('/').pop() || 'image.jpg', {
            type: 'image/jpeg',
          }),
          progress: 100,
          url: url,
        })),
      }));
    }
  }, [branchData, mode]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const { mutate: createBranch, isPending: isCreating } = useMutation({
    mutationFn: (data: BaseCompanyBranch) => branchsAPI.createBranch(data),
    onSuccess: () => {
      toast.success('Branch created successfully');
      navigate('/branch-management');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create branch');
    },
  });

  const { mutate: updateBranch, isPending: isUpdating } = useMutation({
    mutationFn: (data: BaseCompanyBranch) => branchsAPI.updateBranch(id!, data),
    onSuccess: () => {
      toast.success('Branch updated successfully');
      navigate('/branch-management');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update branch');
    },
  });

  const isSubmitting = isCreating || isUpdating;

  const handleSubmit = () => {
    // Filter out any uploads that didn't complete
    const validImageUrls = formData.uploadedFiles
      .filter((file) => file.url)
      .map((file) => file.url as string);

    const dataToSave: BaseCompanyBranch = {
      name: formData.name.trim(),
      address: formData.address.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      isActive: formData.isActive,
      imageUrls: [
        ...new Set([...validImageUrls, ...formData.imageUrls]),
      ].filter(Boolean) as string[],
    };

    if (!dataToSave.name) {
      toast.error('Branch name is required');
      return;
    }

    if (mode === 'edit') {
      updateBranch(dataToSave);
    } else {
      createBranch(dataToSave);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files).map((file) => ({
      file,
      progress: 0,
    }));

    setFormData((prev) => ({
      ...prev,
      uploadedFiles: [...prev.uploadedFiles, ...newFiles],
    }));

    // Upload files
    newFiles.forEach((fileObj, index) => {
      const fileIndex = formData.uploadedFiles.length + index;
      uploadFile(fileObj.file, (progressInfo: UploadProgressInfo) => {
        setFormData((prev) => {
          const updatedFiles = [...prev.uploadedFiles];
          updatedFiles[fileIndex].progress = progressInfo.percentage;
          return { ...prev, uploadedFiles: updatedFiles };
        });
      })
        .then((response) => {
          setFormData((prev) => {
            const updatedFiles = [...prev.uploadedFiles];
            updatedFiles[fileIndex].url = response.url;
            // Add to imageUrls if not already present
            const imageUrls = [...prev.imageUrls];
            if (!imageUrls.includes(response.url)) {
              imageUrls.push(response.url);
            }
            return { ...prev, uploadedFiles: updatedFiles, imageUrls };
          });
        })
        .catch((error) => {
          console.error('Upload failed:', error);
          toast.error(`Failed to upload ${fileObj.file.name}`);
        });
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFormData((prev) => {
      const updatedFiles = [...prev.uploadedFiles];
      const removedFile = updatedFiles.splice(index, 1)[0];
      // Remove from imageUrls if it was successfully uploaded
      const imageUrls = prev.imageUrls.filter((url) => removedFile.url !== url);
      return { ...prev, uploadedFiles: updatedFiles, imageUrls };
    });
  };

  if (isLoading && mode === 'edit') {
    return (
      <div className="flex items-center justify-center h-64">
        Loading branch data...
      </div>
    );
  }

  const title = mode === 'add' ? 'Add New Branch' : 'Edit Branch';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex md:items-center justify-between flex-col md:flex-row gap-4 md:gap-0">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Branches
          </Button>
          <div className="h-6 w-px bg-border" />
          <h1 className="text-lg md:text-2xl font-semibold">{title}</h1>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center flex-1 md:flex-none"
            disabled={isSubmitting}
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex items-center flex-1 md:flex-none"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {mode === 'add' ? 'Creating...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {mode === 'add' ? 'Add Branch' : 'Save Changes'}
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Branch Image Section */}
        <div className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="py-3 sm:py-4">
              <CardTitle className="text-base sm:text-lg">
                Branch Images
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4">
              <div className="aspect-video w-full rounded-lg overflow-hidden bg-muted">
                {formData.imageUrls[0] ? (
                  <ImageWithFallback
                    src={formData.imageUrls[0]}
                    alt={formData.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    No images available
                  </div>
                )}
              </div>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <Label htmlFor="file-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center justify-center p-4 sm:p-6 border-2 border-dashed rounded-lg hover:bg-accent/30 transition-colors">
                      <Upload className="h-7 w-7 sm:h-8 sm:w-8 mb-2 text-muted-foreground" />
                      <p className="text-xs sm:text-sm text-center">
                        <span className="font-medium text-primary">
                          Click to upload
                        </span>{' '}
                        or drag and drop
                      </p>
                      <p className="text-[11px] sm:text-xs text-muted-foreground mt-1">
                        PNG, JPG, JPEG (max 5MB)
                      </p>
                    </div>
                  </Label>
                  <input
                    id="file-upload"
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                  />
                </div>

                {/* Upload progress */}
                <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                  {formData.uploadedFiles.map((fileObj, index) => (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="truncate max-w-[70%]">
                          {fileObj.file.name}
                        </span>
                        <span className="text-muted-foreground">
                          {fileObj.progress}%
                        </span>
                      </div>
                      <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all duration-300"
                          style={{ width: `${fileObj.progress}%` }}
                        />
                      </div>
                      {fileObj.url && (
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-xs text-muted-foreground truncate max-w-[80%]">
                            {fileObj.url}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => removeFile(index)}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Branch Information Form */}
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* <div className="space-y-2">
                  <Label htmlFor="type">Branch Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => handleInputChange('type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flagship">Flagship Store</SelectItem>
                      <SelectItem value="mall">Mall Location</SelectItem>
                      <SelectItem value="outlet">Outlet Store</SelectItem>
                      <SelectItem value="boutique">Boutique</SelectItem>
                      <SelectItem value="express">Express Store</SelectItem>
                    </SelectContent>
                  </Select>
                </div> */}
                <div className="space-y-2">
                  <Label htmlFor="name">Branch Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter branch name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="isActive">Status</Label>
                  <Select
                    value={formData.isActive ? 'active' : 'inactive'}
                    onValueChange={(value) =>
                      handleInputChange('isActive', value === 'active')
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      handleInputChange('phoneNumber', e.target.value)
                    }
                    placeholder="+998901234567"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Full Address</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  rows={3}
                  placeholder="Enter complete address including street, city, state, and ZIP code"
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          {/* <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Phone className="h-5 w-5 mr-2" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="branch@homedecor.com"
                  />
                </div>
              </div>
            </CardContent>
          </Card> */}
        </div>
      </div>

      {/* Additional Information */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              Operating Hours
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="openingHours">Weekday Hours</Label>
              <Input
                id="openingHours"
                value={formData.openingHours}
                onChange={(e) =>
                  handleInputChange('openingHours', e.target.value)
                }
                placeholder="9:00 AM - 8:00 PM"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="weekendHours">Weekend Hours</Label>
              <Input
                id="weekendHours"
                value={formData.weekendHours}
                onChange={(e) =>
                  handleInputChange('weekendHours', e.target.value)
                }
                placeholder="10:00 AM - 7:00 PM"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Staff Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="manager">Branch Manager</Label>
              <Input
                id="manager"
                value={formData.manager}
                onChange={(e) => handleInputChange('manager', e.target.value)}
                placeholder="Manager full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="staff">Total Staff Count</Label>
              <Input
                id="staff"
                type="number"
                value={formData.staff}
                onChange={(e) => handleInputChange('staff', e.target.value)}
                placeholder="0"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Financial Targets
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="monthlyTarget">Monthly Revenue Target</Label>
              <Input
                id="monthlyTarget"
                type="number"
                value={formData.monthlyTarget}
                onChange={(e) =>
                  handleInputChange('monthlyTarget', e.target.value)
                }
                placeholder="0"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              Set the monthly revenue target for this branch to track
              performance.
            </div>
          </CardContent>
        </Card>
      </div> */}
    </div>
  );
}
